import qs from 'qs';
import isUuid from 'is-uuid';
import {
  PositionsConst,
  TimeCommitment as TimeCommitmentConst,
  EngagementLengthConst,
  CountryCodesConst,
  WorkFeedConst,
} from '@axiom/const';
import type {
  Candidate,
  CandidateWorkFeedFormFilter,
  Taxonomy,
} from '@axiom/validation';
import { TaxonomyUtil } from '@axiom/ui';

import { WorkFeedUtil } from './WorkFeedUtil';
import { localStorageUtil } from './local-storage-util';

const { CountryCodes, CountryCodesAbbreviations } = CountryCodesConst;
const { EngagementLengths } = EngagementLengthConst;
const { Phases } = WorkFeedConst;
const { HoursPerWeek } = TimeCommitmentConst;
const { Worksites } = PositionsConst;

const opportunityStageOptions = [
  Phases.New,
  Phases.Submitted,
  Phases.Interviewing,
].map(phase => ({
  value: phase,
  label: WorkFeedUtil.formatPhaseForDisplay(phase),
}));

const weeklyCommitmentOptions = [
  HoursPerWeek.FortyPlus,
  HoursPerWeek.ThirtyOneToForty,
  HoursPerWeek.TwentyOneToThirty,
  HoursPerWeek.ElevenToTwenty,
  HoursPerWeek.OneToTen,
].map(hrsPerWeek => ({
  value: hrsPerWeek,
  label: `${hrsPerWeek} hrs`,
}));

const countryOptions = [
  { label: CountryCodes.US, value: CountryCodesAbbreviations.US },
  { label: CountryCodes.UK, value: CountryCodesAbbreviations.UK },
  { label: CountryCodes.CA, value: CountryCodesAbbreviations.CA },
  { label: CountryCodes.SG, value: CountryCodesAbbreviations.SG },
  { label: CountryCodes.HK, value: CountryCodesAbbreviations.HK },
  { label: CountryCodes.DE, value: CountryCodesAbbreviations.DE },
  { label: CountryCodes.CH, value: CountryCodesAbbreviations.CH },
];

const worksiteOptions = [
  { value: Worksites.Remote, label: 'Remote' },
  { value: Worksites.OnsiteRemote, label: 'Hybrid' },
  { value: Worksites.Onsite, label: 'Onsite' },
];

const engagementLengthOptions = [
  EngagementLengths.ZeroToThree,
  EngagementLengths.FourToSix,
  EngagementLengths.SevenToNine,
  EngagementLengths.TenToTwelve,
  EngagementLengths.TwelvePlus,
].map(el => ({
  value: el,
  label: `${el} months`,
}));

const getInitialFilters = (
  candidate: Candidate,
  taxonomy: Taxonomy
): Required<
  Pick<
    CandidateWorkFeedFormFilter,
    'countries' | 'practiceAreas' | 'focusAreas' | 'businessTitles'
  >
> => {
  return {
    countries:
      candidate.addressCountryCode &&
      countryOptions.some(o => o.value === candidate.addressCountryCode)
        ? [candidate.addressCountryCode]
        : [],
    practiceAreas:
      candidate.primaryPracticeArea &&
      TaxonomyUtil.isPracticeAreaSelectable(
        taxonomy,
        candidate.primaryPracticeArea
      )
        ? [candidate.primaryPracticeArea]
        : [],
    focusAreas:
      candidate.primaryFocusArea &&
      TaxonomyUtil.isFocusAreaSelectable(taxonomy, candidate.primaryFocusArea)
        ? [candidate.primaryFocusArea]
        : [],
    businessTitles: candidate.businessTitle ? [candidate.businessTitle] : [],
  };
};

// Clears out any filters that were transitioned from UUIDs to enums
const washFilters = (filters: CandidateWorkFeedFormFilter) => {
  const transitionedFilters: Array<keyof CandidateWorkFeedFormFilter> = [
    'industries',
  ];

  transitionedFilters.forEach(fil => {
    const filter = filters[fil];
    if (filter?.some(opt => isUuid.anyNonNil(opt))) {
      delete filters[fil];
    }
  });

  return filters;
};

// qs by default converts arrays with > 20 items to an object with the index as the key
// the limit is increased here since industries can have up to 36 selections
const getFilters = (
  searchParams: URLSearchParams,
  candidate: Candidate,
  taxonomy: Taxonomy
): CandidateWorkFeedFormFilter => {
  const { countries, practiceAreas, focusAreas, businessTitles } =
    getInitialFilters(candidate, taxonomy);

  return searchParams.has('filters')
    ? washFilters(
        qs.parse(searchParams.get('filters') ?? '', { arrayLimit: 40 })
      )
    : localStorageUtil
        .workFeedFilters(
          candidate.id,
          practiceAreas,
          focusAreas,
          countries,
          businessTitles
        )
        .get();
};

const applyFilters = (
  setSearchParams: (arg: { filters: string }) => void,
  candidate: Candidate,
  taxonomy: Taxonomy,
  filters?: Record<string, string | string[] | null> | null
) => {
  setSearchParams({
    filters: filters ? qs.stringify(filters) : '',
  });
  const defaultFilters = getInitialFilters(candidate, taxonomy);

  localStorageUtil.workFeedFilters(candidate.id).set({
    ...filters,
    countries: filters?.countries?.length
      ? filters.countries
      : defaultFilters.countries,
    practiceAreas: filters?.practiceAreas?.length
      ? filters.practiceAreas
      : defaultFilters.practiceAreas,
    focusAreas: filters?.focusAreas?.length
      ? filters.focusAreas
      : defaultFilters.focusAreas,
    businessTitles: filters?.businessTitles?.length
      ? filters.businessTitles
      : defaultFilters.businessTitles,
  });
};

export const WorkFeedFilterUtil = {
  getFilters,
  applyFilters,
  washFilters,
  opportunityStageOptions,
  weeklyCommitmentOptions,
  countryOptions,
  worksiteOptions,
  engagementLengthOptions,
};
